import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../../contexts/AuthContext'
import { useIdleTimeout } from '../../auth/components/IdleTimeoutProvider'
import MFASettings from '../../auth/components/MFASettings'
import { supabase } from '../../../shared/lib/supabase'

function FrameworkStagingPanel() {
  const { user } = useAuth()
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('pending')
  const [actionLoading, setActionLoading] = useState(null)

  const fetchEntries = useCallback(async () => {
    setLoading(true)
    let query = supabase
      .from('framework_staging')
      .select('*, clients(name)')
      .order('created_at', { ascending: false })

    if (filter !== 'all') {
      query = query.eq('status', filter)
    }

    const { data } = await query
    setEntries(data || [])
    setLoading(false)
  }, [filter])

  useEffect(() => { fetchEntries() }, [fetchEntries])

  const handleApprove = async (entry) => {
    setActionLoading(entry.id)
    try {
      // Create a new compliance_frameworks record from the staged submission
      const { data: newFramework, error: createError } = await supabase
        .from('compliance_frameworks')
        .insert({
          org_id: entry.org_id,
          name: entry.submitted_name,
          abbreviation: entry.submitted_name,
          category: 'regulatory',
          created_by: user?.id
        })
        .select('id')
        .single()

      if (createError) {
        console.error('[FrameworkStaging] Create framework failed:', createError.message)
        setActionLoading(null)
        return
      }

      // Update staging entry
      await supabase
        .from('framework_staging')
        .update({
          status: 'approved',
          resolved_framework_id: newFramework.id,
          resolved_by: user?.id,
          resolved_at: new Date().toISOString()
        })
        .eq('id', entry.id)

      fetchEntries()
    } finally {
      setActionLoading(null)
    }
  }

  const handleReject = async (entry) => {
    setActionLoading(entry.id)
    try {
      await supabase
        .from('framework_staging')
        .update({
          status: 'rejected',
          resolved_by: user?.id,
          resolved_at: new Date().toISOString()
        })
        .eq('id', entry.id)

      fetchEntries()
    } finally {
      setActionLoading(null)
    }
  }

  const pendingCount = entries.filter(e => e.status === 'pending').length

  const statusColors = {
    pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    approved: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    rejected: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Framework Staging</h2>
          {filter === 'pending' && pendingCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
              {pendingCount} pending
            </span>
          )}
        </div>
        <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
          {['pending', 'approved', 'rejected', 'all'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors capitalize ${
                filter === f
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Custom framework names submitted during client intake or assessments. Approve to add them as recognized compliance frameworks.
      </p>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400 dark:text-gray-500">
            <div className="animate-spin w-6 h-6 border-2 border-gray-300 dark:border-gray-600 border-t-purple-500 rounded-full mx-auto mb-2" />
            Loading...
          </div>
        ) : entries.length === 0 ? (
          <div className="p-8 text-center">
            <svg className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {filter === 'pending' ? 'No frameworks awaiting review' : `No ${filter} entries`}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <th className="text-left px-4 py-2.5 font-medium text-gray-500 dark:text-gray-400">Framework Name</th>
                <th className="text-left px-4 py-2.5 font-medium text-gray-500 dark:text-gray-400">Client</th>
                <th className="text-left px-4 py-2.5 font-medium text-gray-500 dark:text-gray-400">Source</th>
                <th className="text-left px-4 py-2.5 font-medium text-gray-500 dark:text-gray-400">Status</th>
                <th className="text-left px-4 py-2.5 font-medium text-gray-500 dark:text-gray-400">Submitted</th>
                {filter === 'pending' && (
                  <th className="text-right px-4 py-2.5 font-medium text-gray-500 dark:text-gray-400">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {entries.map(entry => (
                <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{entry.submitted_name}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{entry.clients?.name || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      entry.submitted_by === 'client_intake'
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                        : 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
                    }`}>
                      {entry.submitted_by === 'client_intake' ? 'Client Intake' : 'Assessor'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${statusColors[entry.status] || ''}`}>
                      {entry.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                    {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : '—'}
                  </td>
                  {filter === 'pending' && (
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleApprove(entry)}
                          disabled={actionLoading === entry.id}
                          className="px-3 py-1 text-xs font-medium rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
                        >
                          {actionLoading === entry.id ? '...' : 'Approve'}
                        </button>
                        <button
                          onClick={() => handleReject(entry)}
                          disabled={actionLoading === entry.id}
                          className="px-3 py-1 text-xs font-medium rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
                        >
                          Reject
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function Settings() {
  const { profile } = useAuth()
  const { refreshMFAStatus } = useIdleTimeout()

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Manage your account settings and security preferences
        </p>
      </div>

      {/* Account Info */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Account Information</h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700">
            <span className="text-sm text-gray-500 dark:text-gray-400">Name</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">{profile?.full_name || '—'}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700">
            <span className="text-sm text-gray-500 dark:text-gray-400">Email</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">{profile?.email || '—'}</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-gray-500 dark:text-gray-400">Role</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">{profile?.role || '—'}</span>
          </div>
        </div>
      </div>

      {/* Security Section */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Security</h2>
        <MFASettings onMFAChange={refreshMFAStatus} />

        {/* Idle Timeout Info */}
        <div className="card p-6 mt-4">
          <div className="flex items-start space-x-4">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/40 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Session Timeout
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                For security, you will be asked to verify your identity with your authenticator app after 4 hours of inactivity.
                This only applies when two-factor authentication is enabled.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Framework Staging Section */}
      <FrameworkStagingPanel />
    </div>
  )
}

export default Settings
